import React from 'react';

import {RequiredMark} from 'src/components/required-mark';
import {ApprovalRequestSearchItem} from 'src/openapi/admin/models/ApprovalRequestSearchItem';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Dialog, DialogFooter, DialogHeader} from '@pb/kit-component-dialog-v2';
import {FieldDescription} from '@pb/kit-component-field-description';
import Group from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Textarea} from '@pb/kit-component-textarea-v2';
import Typography from '@pb/kit-component-typography';

type Props = {
    shown: boolean,
    onClose: () => void,
    onConfirm: (requestId: string, reason: string) => void,
    request: ApprovalRequestSearchItem,
};

const MAX_OTHER_COMMENT_LENGTH = 100;

const OTHER_REASON = 'Other (additional details required)';

const REASONS = [
    'User does not qualify for real-time research',
    'User or client not recognized',
    'Firm or broker is restricted',
    OTHER_REASON,
] as const;


export const DenyRequestDialog = (props: Props) => {
    const {shown, onClose, onConfirm, request} = props;
    const [selectedReason, setSelectedReason] = React.useState<string>('');
    const [otherComment, setOtherComment] = React.useState('');

    const isOtherReason = selectedReason === OTHER_REASON;
    const trimmedOtherComment = React.useMemo(() => otherComment.trim(), [otherComment]);
    const isOtherCommentTooLong = React.useMemo(() => otherComment.length > MAX_OTHER_COMMENT_LENGTH, [otherComment]);
    const isSubmitDisabled = React.useMemo(() => {
        if (!request.requestId || !selectedReason) {
            return true;
        }
        if (!isOtherReason) {
            return false;
        }
        return !trimmedOtherComment || isOtherCommentTooLong;
    }, [isOtherCommentTooLong, isOtherReason, trimmedOtherComment, selectedReason, request.requestId]);

    const handleDeny = () => {
        if (isSubmitDisabled) {
            return;
        }
        const reasonText = isOtherReason ? trimmedOtherComment : selectedReason;
        onConfirm(request.requestId, reasonText);
        onClose();
    };

    const helperTextId = 'deny-reason-helper';
    const otherHelperTextId = 'deny-reason-other-helper';
    const remainingOther = MAX_OTHER_COMMENT_LENGTH - otherComment.length;

    return (
        <Dialog
            shown={shown}
            setShown={visible => {
                if (!visible) {
                    onClose();
                }
            }}
            wrapperProps={{style: {width: 600}}}>
            <DialogHeader
                onClose={onClose}
                style={{whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip'}}>
                <Typography variant="heading-level-2">
                    {`Deny ${request.requestUserInfo?.email ?? ''}'s request?`}
                </Typography>
            </DialogHeader>
            <Group direction="v" gap="m" style={{padding: 24}}>
                <span>Are you sure you want to deny this request?</span>
                <Group direction="v">
                    <Group gap="xs">
                        <strong>Requested by:</strong>
                        <Group gap="xs">
                            <span>{`${request.requestUserInfo?.firstName ?? ''} ${request.requestUserInfo?.lastName ?? ''}`.trim()}</span>
                            <span>{request.requestUserInfo?.email ?? ''}</span>
                        </Group>
                    </Group>
                    <Label htmlFor="deny-request-reason">
                        <RequiredMark label="Please select a reason for denying this request"/>
                    </Label>
                    <Group direction="v" gap="xxs">
                        <ComboBox<string, string>
                            id="deny-request-reason"
                            value={selectedReason}
                            onChange={val => setSelectedReason(Array.isArray(val) ? val[0] : val)}
                            placeholder="Select reason"
                            aria-label="Reason for denying request"
                            aria-describedby={helperTextId}
                            style={{minWidth: 300}}>
                            <ComboboxItem caption="Select reason" value=""/>
                            {REASONS.map(reason => (
                                <ComboboxItem key={reason} caption={reason} value={reason}/>
                            ))}
                        </ComboBox>
                    </Group>
                    {isOtherReason ? (
                        <Group direction="v" gap="xxs">
                            <Label htmlFor="deny-request-other-details">
                                <RequiredMark label="Please provide additional details (max 100 characters)"/>
                            </Label>
                            <Textarea
                                id="deny-request-other-details"
                                value={otherComment}
                                onChange={e => setOtherComment(e.target.value)}
                                placeholder="Enter additional details"
                                resizable={false}
                                color={isOtherCommentTooLong ? 'ERROR' : 'DEFAULT'}
                                maxLength={MAX_OTHER_COMMENT_LENGTH}
                                aria-label="Additional details for denying request"
                                aria-describedby={otherHelperTextId}
                            />
                            <FieldDescription
                                id={otherHelperTextId}
                                aria-live="polite"
                                color={isOtherCommentTooLong ? 'Types.ERROR' : 'Types.DEFAULT'}>
                                {isOtherCommentTooLong ? 'Maximum length of 100 characters exceeded' : `${remainingOther} characters left`}
                            </FieldDescription>
                        </Group>
                    ) : null}
                </Group>
            </Group>
            <DialogFooter>
                <Button variant="Type.PRIMARY" onClick={handleDeny} disabled={isSubmitDisabled}>Deny</Button>
                <Button variant="Type.SECONDARY" onClick={onClose}>Cancel</Button>
            </DialogFooter>
        </Dialog>
    );
};
